import type { UserRole } from '@yrp/shared/user';
import type { z } from 'zod';
import type { WebsiteActivityQuery } from '@yrp/shared/website-activity';

export const ACTIVITY_WINDOWS = [7, 30, 90] as const;
export type ActivityWindow = z.infer<typeof WebsiteActivityQuery>['days'];
/** The page's tabs, in display order. */
export const ACTIVITY_TABS = ['overview', 'people', 'visitors', 'homes', 'sources'] as const;
export type ActivityTab = (typeof ACTIVITY_TABS)[number];
/** Older `?view=` values that saved links and the admin health banner still use. */
export const LEGACY_ACTIVITY_VIEWS = ['registrations', 'listings', 'acquisition', 'coverage'] as const;
/** Opens Overview with "How we count these numbers" open at the top (linked from the CRM contact page). */
export const DRAWER_ACTIVITY_VIEWS = ['how'] as const;
/** Every `?view=` value the route accepts: the tabs, the legacy names and the drawer link. */
export const ACTIVITY_VIEWS = [...ACTIVITY_TABS, ...LEGACY_ACTIVITY_VIEWS, ...DRAWER_ACTIVITY_VIEWS] as const;
export type ActivityView = (typeof ACTIVITY_VIEWS)[number];
const TAB_LABELS: Readonly<Record<ActivityTab, string>> = { overview: 'Overview', people: 'People', visitors: 'Visitors', homes: 'Homes', sources: 'Sources' };

export function activityWindow(value: unknown): ActivityWindow {
  return value === 7 || value === '7' ? 7 : value === 90 || value === '90' ? 90 : 30;
}

export function activityView(value: unknown): ActivityView {
  return ACTIVITY_VIEWS.find((view) => view === value) ?? 'overview';
}

export function activityViews(role: UserRole | undefined): readonly ActivityView[] {
  return ACTIVITY_VIEWS.filter((view) => view !== 'visitors' || role === 'admin');
}

export function activityViewForRole(value: unknown, role: UserRole | undefined): ActivityView {
  const view = activityView(value);
  return view === 'visitors' && role !== 'admin' ? 'overview' : view;
}

/**
 * Map a URL view (new or legacy) to the tab it opens. `coverage` used to be a
 * page of caveats; it now opens Overview with the "How we count" drawer open.
 */
export function activityTabForView(view: ActivityView): { tab: ActivityTab; howWeCount: boolean } {
  switch (view) {
    case 'registrations': return { tab: 'people', howWeCount: false };
    case 'listings': return { tab: 'homes', howWeCount: false };
    case 'acquisition': return { tab: 'sources', howWeCount: false };
    case 'coverage': case 'how': return { tab: 'overview', howWeCount: true };
    default: return { tab: view, howWeCount: false };
  }
}

/** Visitors (anonymous browsers, IPs) exists only for an admin who explicitly chose Entire brokerage. */
export function activityTabs(role: UserRole | undefined, all: boolean): readonly ActivityTab[] {
  return ACTIVITY_TABS.filter((tab) => tab !== 'visitors' || activityAll(role, all));
}

/** The tab actually shown: a hidden Visitors tab falls back to Overview. */
export function activityTabShown(tab: ActivityTab, role: UserRole | undefined, all: boolean): ActivityTab {
  return activityTabs(role, all).includes(tab) ? tab : 'overview';
}

export function activityTabLabel(tab: ActivityTab): string {
  return TAB_LABELS[tab];
}

export function activitySearch(search: Record<string, unknown>): { days?: ActivityWindow; view?: ActivityView } {
  return { days: activityWindow(search.days), view: activityView(search.view) };
}

export function contactActivitySearch(search: Record<string, unknown>): { tab?: 'website'; compose?: 'popout' } {
  return { ...(search.tab === 'website' ? { tab: 'website' as const } : {}), ...(search.compose === 'popout' ? { compose: 'popout' as const } : {}) };
}

/** An admin's explicit choice is required on every scope transition. */
export function activityAll(role: UserRole | undefined, requested: boolean): boolean {
  return role === 'admin' && requested;
}

export function activityQuery(days: ActivityWindow, all: boolean, cursor?: string): string {
  const query = new URLSearchParams({ days: String(days) });
  if (all) query.set('all', 'true');
  if (cursor && /^\d{1,16}$/.test(cursor)) query.set('cursor', cursor);
  return query.toString();
}

export function activityDuration(ms: number): string {
  if (!Number.isFinite(ms) || ms < 0) return 'Unavailable';
  if (ms === 0) return '0s';
  if (ms < 1000) return '<1s';
  const seconds = Math.floor(ms / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ${seconds % 60}s`;
  return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
}

export function activityDate(value: string | null): string {
  if (!value) return 'Not recorded';
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return 'Unavailable';
  return date.toLocaleString('en-US', {
    timeZone: 'America/Phoenix', month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
  });
}

const AZ = 'America/Phoenix';
function arizonaDayKey(date: Date): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: AZ, year: 'numeric', month: '2-digit', day: '2-digit' }).format(date);
}

/** Short Arizona-time label for lists: "Today 9:09 PM", "Yesterday", "Oct 26", or "Oct 26, 2025". */
export function activityShortDate(value: string | null | undefined, now: number = Date.now()): string | null {
  if (!value) return null;
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return null;
  const today = new Date(now);
  const day = arizonaDayKey(date);
  if (day === arizonaDayKey(today)) return `Today ${date.toLocaleTimeString('en-US', { timeZone: AZ, hour: 'numeric', minute: '2-digit' })}`;
  if (day === arizonaDayKey(new Date(now - 86_400_000))) return 'Yesterday';
  const sameYear = day.slice(0, 4) === arizonaDayKey(today).slice(0, 4);
  return date.toLocaleDateString('en-US', { timeZone: AZ, month: 'short', day: 'numeric', ...(sameYear ? {} : { year: 'numeric' }) });
}

/** activityShortDate for the middle of a sentence: "last today 9:09 PM", "last yesterday", "last Oct 26". */
export function activityShortDateInline(value: string | null | undefined, now: number = Date.now()): string | null {
  return activityShortDate(value, now)?.replace(/^(Today|Yesterday)/, (word) => word.toLowerCase()) ?? null;
}

/** "Nov 3, 9:38 PM" in Arizona time, for the "Updated …" line. */
export function activityUpdated(value: string | null | undefined): string | null {
  if (!value) return null;
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return null;
  return date.toLocaleString('en-US', { timeZone: AZ, month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

export function plural(count: number, one: string, many = `${one}s`): string {
  return `${count.toLocaleString()} ${count === 1 ? one : many}`;
}

export function marketLabel(market: string | null | undefined): string {
  return market === 'yuma' ? 'Yuma' : market === 'tucson' ? 'Tucson' : market ? market : '';
}

export function activityIdentity(value: string): string {
  if (value === 'authenticated') return 'Signed-in account';
  if (value === 'same_browser_before_sign_in') return 'Same browser before sign-in';
  if (value === 'submitted_email') return 'Email submitted — unverified';
  if (value === 'anonymous') return 'Anonymous browser';
  return 'Identity not established';
}

const EVENT_LABELS: Readonly<Record<string, string>> = {
  visit: 'Started a visit', visit_started: 'Started a visit', sign_in: 'Signed in', sign_out: 'Signed out',
  page_view: 'Opened a page', listing_view: 'Viewed a listing', active_time: 'Active browsing',
  search: 'Searched for homes', favorite_added: 'Saved a home', favorite_removed: 'Removed a saved home',
  saved_search_created: 'Saved a search', saved_search_updated: 'Updated a search', saved_search_deleted: 'Deleted a search',
  share_opened: 'Opened sharing options', share_action: 'Used a sharing option', share: 'Used a sharing option',
  inquiry: 'Sent an inquiry', tour_requested: 'Requested a showing', valuation_requested: 'Requested a valuation',
  lead_submitted: 'Website form submitted', registration_completed: 'Completed registration', interaction: 'Used a website feature',
  gallery_opened: 'Opened listing photos', tour_opened: 'Opened a virtual tour', video_started: 'Started a listing video',
  map_interacted: 'Used the map', cta_clicked: 'Used a listing action',
};

export function activityEventLabel(type: string): string {
  return EVENT_LABELS[type] ?? type.replaceAll('_', ' ');
}

/** Canonical listing identity only. No arbitrary URLs or capability tokens. */
export function activityListingParams(market: string | null, key: string | null): { market: string; key: string } | null {
  if ((market !== 'yuma' && market !== 'tucson') || !key || !/^[a-zA-Z0-9_-]+$/.test(key)) return null;
  return { market, key };
}

function errorStatus(error: unknown): unknown {
  return error && typeof error === 'object' && 'statusCode' in error ? error.statusCode : undefined;
}

export function activityAccessDenied(error: unknown): boolean {
  const status = errorStatus(error);
  return status === 403 || status === 404;
}

/** 404 only. Where the caller is already known to have access (a visitor page), it means "nothing in this period". */
export function activityNotFound(error: unknown): boolean {
  return errorStatus(error) === 404;
}

export function activityError(error: unknown): string {
  if (activityAccessDenied(error)) return 'Website activity is unavailable for this scope. Contact access does not automatically grant access to website activity.';
  return 'Website activity could not be loaded. These are unavailable results, not zero activity. Try again.';
}
